<template>
  <div class="properties-body">
    <loader :isShow="isLoading && hasLoaded && !isFiltering" :textInfo="''" />
    <section class="properties-list-top-container">
      <div class="properties-list-top-container-title">My Properties</div>

      <div class="rooms-nav-container">
        <div class="right">
          <v-popover
            class="trigger"
            offset="16"
            :auto-hide="true"
            placement="bottom-end"
          >
            <button class="select-btn">
              <span>Occupancy</span>
              <img class="down-arrow" src="@/assets/DownGreyArrow.svg" />
            </button>

            <template slot="popover">
              <GenericSearchCheckBoxMenu
                @occupancy-checked="filterOccupancy"
                :checked="filteredOccupancy"
                displayProperty="name"
                :things="occupancy"
                searchThing="occupancy"
                displaySearch="false"
                :key="'calendar-property-filter'"
              />
            </template>
          </v-popover>
        </div>
        <Button class="properties-list-top-container-button" @click="createNew">
          New Property
        </Button>
      </div>
    </section>

    <section v-if="showSkeleton" class="properties-list-loading">
      <PropertiesSkeleton />
    </section>

    <section
      v-if="!showSkeleton && !noProperties"
      :key="propertyListKey"
      class="properties-list-grid"
      :style="styleContainer()"
    >
      <PropertyCell
        v-for="property of this.properties"
        :key="property.uuid"
        @delete="showDeletePrompt"
        @loading="isLoading = $event"
        :property="property ? property : null"
      />
    </section>

    <delete-popup
      @confirm="deleteProperty"
      @cancel="hideDeletePrompt"
      v-if="isDeleting"
      :thingName="'Property'"
    />

    <section v-if="showEmptyState" class="empty-property-screen">
      <img src="../../assets/empty_property_icon.svg" alt="" />
      <div>Add your first property</div>
      <Button class="properties-list-top-container-button" @click="createNew">
        New Property
      </Button>
    </section>

    <section v-if="showNoFilterResults" class="empty-property-screen">
      <img src="../../assets/no_filtered_chores.svg" alt="" />
      <div>No properties match your filters</div>
      <Button
        class="properties-list-top-container-button"
        @click="clearOccupancyFilter"
      >
        Clear Filters
      </Button>
    </section>
  </div>
</template>

<script>
import { Button } from 'ant-design-vue'
import calls from '@/api/calls'
import { auth } from '../../Firebase'
import PropertyCell from '@/components/properties/PropertyCell.vue'
import DeletePopup from '../../components/GenericDeletePopup.vue'
import GenericSearchCheckBoxMenu from '@/components/GenericSearchCheckBoxMenu'
import Loader from '@/components/Loader.vue'
import PropertiesSkeleton from '@/components/shared/PropertiesSkeleton.vue'
import { getCompleteProperties } from '../../api/properties'

export default {
  name: 'MyPropertiesScreen',
  components: {
    PropertyCell,
    Loader,
    PropertiesSkeleton,
    DeletePopup,
    GenericSearchCheckBoxMenu,
    Button
  },
  data() {
    return {
      properties: [],
      isLoading: true,
      hasLoaded: false,
      isFiltering: false,
      filterDebounceMs: 800,
      filterDebounceTimer: null,
      filterDirty: false,
      isDeleting: false,
      selPropertyId: null,
      propertyListKey: 0,
      vw: 0,
      vh: 0,
      filteredOccupancy: ['green', 'yellow', 'red'],
      occupancy: [
        {
          uuid: 'green',
          name: '>= 90%'
        },
        {
          uuid: 'yellow',
          name: '>= 70% and < 90%'
        },
        {
          uuid: 'red',
          name: '< 70%'
        }
      ]
    }
  },
  computed: {
    noProperties() {
      return this.properties.length === 0
    },
    isInitialLoading() {
      return this.isLoading && !this.hasLoaded
    },
    showSkeleton() {
      return this.isInitialLoading || this.isFiltering
    },
    isFilterActive() {
      return this.filteredOccupancy.length !== this.occupancy.length
    },
    showEmptyState() {
      return (
        this.hasLoaded &&
        !this.isLoading &&
        this.noProperties &&
        !this.isFilterActive
      )
    },
    showNoFilterResults() {
      return (
        this.hasLoaded &&
        !this.isLoading &&
        this.noProperties &&
        this.isFilterActive
      )
    }
  },
  methods: {
    async deleteProperty() {
      await calls.DELETE(`properties/${this.selPropertyId}`)
      await this.reload()
    },
    resize() {
      this.vw = Math.max(
        document.documentElement.clientWidth || 0,
        window.innerWidth || 0
      )
      this.vh = Math.max(
        document.documentElement.clientHeight || 0,
        window.innerHeight || 0
      )
    },
    styleContainer() {
      let size = parseInt(Math.floor(this.properties.length / 4))
      let cardsPerRow = parseInt(Math.floor(285 / this.vw))
      let numberRows = parseInt(this.properties.length / cardsPerRow)
      if (numberRows < 1) {
        numberRows = 1
      }
      numberRows += 1
      return {
        'grid-template-rows': `repeat(${size + 1}, 350px)`,
        height: `${350 * numberRows + 27 * 350 * numberRows}px`
      }
    },
    createNew() {
      this.isLoading = true
      this.$router.replace({ name: 'editProperty' })
    },
    showDeletePrompt(uuid) {
      this.isDeleting = true
      this.selPropertyId = uuid
    },
    hideDeletePrompt() {
      this.isDeleting = false
      this.selPropertyId = null
    },
    async reload() {
      this.hideDeletePrompt()
      this.cancelPendingFilter()
      await this.loadCompleteProperties()
      this.propertyListKey += 1
    },
    async loadCompleteProperties() {
      const { error, data } = await getCompleteProperties(
        auth.currentUser.email,
        {
          occupancy:
            this.filteredOccupancy.length > 0
              ? this.filteredOccupancy.join(',')
              : ''
        }
      )
      if (error && error !== false) {
        this.$notify({
          group: 'rent-mgt-notification',
          type: 'error',
          text: error.message
        })
        return
      }
      if (error === false) {
        this.properties = (data || []).map((element) => ({
          ...element,
          occupancy: element.percentage,
          colorOccupancy: element.color
        }))
      }
    },
    createNewProperty() {
      this.$emit('onCreateNewProperty', 'NewProperty')
    },
    cancelPendingFilter() {
      if (this.filterDebounceTimer) {
        clearTimeout(this.filterDebounceTimer)
        this.filterDebounceTimer = null
      }
    },
    scheduleFilteredReload() {
      this.cancelPendingFilter()
      this.filterDebounceTimer = setTimeout(async () => {
        this.filterDebounceTimer = null
        if (this.isFiltering) {
          // A fetch is still in-flight: refetch once with the latest
          // filters when it finishes instead of stacking requests.
          this.filterDirty = true
          return
        }
        this.isFiltering = true
        this.isLoading = true
        try {
          do {
            this.filterDirty = false
            await this.loadCompleteProperties()
          } while (this.filterDirty)
        } finally {
          this.isFiltering = false
          this.isLoading = false
        }
      }, this.filterDebounceMs)
    },
    filterOccupancy(id) {
      if (this.filteredOccupancy.includes(id)) {
        const index = this.filteredOccupancy.indexOf(id)
        this.filteredOccupancy.splice(index, 1)
      } else {
        this.filteredOccupancy.push(id)
      }
      this.scheduleFilteredReload()
    },
    async clearOccupancyFilter() {
      this.cancelPendingFilter()
      this.filteredOccupancy = this.occupancy.map((item) => item.uuid)
      this.isFiltering = true
      this.isLoading = true
      try {
        await this.loadCompleteProperties()
      } finally {
        this.isFiltering = false
        this.isLoading = false
      }
    }
  },
  activated() {
    if (this.hasLoaded) {
      this.isLoading = false
    }
  },
  async mounted() {
    this.isLoading = true
    try {
      await this.loadCompleteProperties()
    } finally {
      this.hasLoaded = true
      this.isLoading = false
    }
    this.resize()
  },
  destroyed() {
    window.removeEventListener('resize', this.resize)
    this.cancelPendingFilter()
  },
  created() {
    window.addEventListener('resize', this.resize)
  }
}
</script>

<style scoped>
.properties-body {
  background-color: #f5f6f8;
  display: flex;
  flex-flow: column nowrap;
  padding: 24px;
}

.properties-list-top-container {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  height: 40px;
  margin-bottom: 32px;
}

.properties-list-top-container-title {
  font-family: Montserrat;
  font-style: normal;
  font-weight: 800;
  font-size: 24px;
  line-height: 40px;
  display: flex;
  align-items: center;
  letter-spacing: 0.8px;
  color: #000000;
}

.properties-list-top-container-button {
  width: 160px;
  height: 40px;
  font-family: Montserrat;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 28px;
  text-align: center;
  color: #ffffff;
  background: #66b015;
  border-radius: 4px;
  border: none;
}

.properties-list-top-container-button:hover {
  cursor: pointer;
}

.properties-list-grid {
  display: grid;
  gap: 12px;
  grid-auto-flow: row;
  grid-template-columns: repeat(auto-fill, 270px);
  overflow-y: auto;
  justify-content: center;
}

.properties-list-loading {
  width: 100%;
}

.empty-property-screen {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
  text-align: center;
}

.empty-property-screen img {
  display: block;
  width: 280px;
  max-width: 100%;
  height: auto;
  max-height: 30vh;
  object-fit: contain;
}

.empty-property-screen div {
  font-family: Montserrat;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 40px;
}

@media screen and (max-width: 648px) {
  .properties-list-grid {
    grid-template-columns: none !important;
    grid-template-rows: none !important;
  }

  .property-cell-buttons-container {
    justify-content: unset;
  }
}

.rooms-nav-container {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: baseline;
  width: 296px;
}

/* styles for new filter */

.right .down-arrow {
  position: absolute;
  right: 10px;
  top: 18px;
}

.right .select-btn {
  position: relative;
  width: 120px;
  height: 40px;
  background: #ffffff;
  border: 1px solid #cbd4dc;
  border-radius: 4px;
  color: #767675;
  font-size: 14px;
  margin-left: 16px;
  text-align: left;
  cursor: pointer;
  margin-right: 10px;
  padding-left: 16px;
}

.right .new-chore {
  background: #66b015;
  border-radius: 4px;
  color: #ffffff;
  font-weight: 400;
  font-size: 14px;
  width: 160px;
  border: 1px solid #66b015;
  height: 40px;
  margin-left: 16px;
  cursor: pointer;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .properties-body {
    padding: 16px;
    overflow: auto;
  }

  .properties-list-top-container {
    flex-direction: column;
    height: auto;
    gap: 16px;
    margin-bottom: 24px;
    align-items: flex-start;
  }

  .properties-list-top-container-title {
    font-size: 20px;
    line-height: 28px;
    margin-bottom: 8px;
  }

  .rooms-nav-container {
    width: 100%;
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .right {
    width: 100%;
  }

  .right .select-btn {
    width: 100% !important;
    box-sizing: border-box !important;
    margin-left: 0;
    margin-right: 0;
    height: 44px;
    font-size: 16px;
  }

  .properties-list-top-container-button {
    width: 100% !important;
    height: 44px;
    font-size: 16px;
    box-sizing: border-box !important;
  }

  .properties-list-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
    padding: 0 8px;
    margin-bottom: 6rem;
  }

  .empty-property-screen {
    padding: 12px 0;
  }

  .empty-property-screen div {
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 8px;
  }

  /* Trigger class for dropdown button */
  .trigger {
    width: 100% !important;
    display: flex !important;
  }
}

@media (max-width: 480px) {
  .properties-body {
    padding: 12px;
  }

  .properties-list-top-container-title {
    font-size: 18px;
    line-height: 24px;
  }

  .properties-list-grid {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 0;
  }

  .right .select-btn {
    height: 40px;
    font-size: 16px;
  }

  .properties-list-top-container-button {
    height: 40px;
    font-size: 16px;
  }
}
</style>
